import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ActivityLogProvider } from './context/ActivityLogContext';
import { CompanyThemeProvider } from './context/CompanyThemeContext';
import { NotificationConfigProvider } from './context/NotificationConfigContext';
import { LanguageProvider } from './context/LanguageContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Dashboard from './pages/Dashboard';
import Loans from './pages/Loans';
import LoanDetail from './pages/LoanDetail';
import Alerts from './pages/Alerts';
import Reports from './pages/Reports';
import Import from './pages/Import';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Help from './pages/Help';
import Team from './pages/Team';
import CompanySettings from './pages/CompanySettings';
import NotificationSettings from './pages/NotificationSettings';
import RolesPermissions from './pages/RolesPermissions';
import Waivers from './pages/Waivers';
import ESG from './pages/ESG';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Unauthorized from './pages/Unauthorized';
import AcceptInvitation from './pages/AcceptInvitation';


const AuthenticatedLayout = ({ children }) => {
  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      <Sidebar />
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <Header />
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};


const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const publicPaths = ['/login', '/register', '/forgot-password', '/unauthorized', '/accept-invitation'];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith('/accept-invitation');


  if (!isLoading && !isAuthenticated && !isPublicPage) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isPublicPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
      </Routes>
    );
  }

  return (
    <AuthenticatedLayout>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loans"
          element={
            <ProtectedRoute requiredPermission="view_loans">
              <Loans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loans/:id"
          element={
            <ProtectedRoute requiredPermission="view_loans">
              <LoanDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute requiredPermission="view_alerts">
              <Alerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredPermission="view_reports">
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={
            <ProtectedRoute requiredPermission="import_data">
              <Import />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Help />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute requiredRole="agent">
              <Team />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company"
          element={
            <ProtectedRoute requiredPermission="manage_settings">
              <CompanySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute requiredRole="agent">
              <NotificationSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute requiredRole="agent">
              <RolesPermissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/waivers"
          element={
            <ProtectedRoute requiredPermission="view_loans">
              <Waivers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/esg"
          element={
            <ProtectedRoute>
              <ESG />
            </ProtectedRoute>
          }
        />
        {}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <NotFound />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthenticatedLayout>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CompanyThemeProvider>
          <NotificationConfigProvider>
            <ActivityLogProvider>
              <Router>
                <AppRoutes />
              </Router>
            </ActivityLogProvider>
          </NotificationConfigProvider>
        </CompanyThemeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;